import React, { useContext, useRef } from 'react';

import { theme } from 'config/theme';
import { CanvasUnitContext } from '../canvasScale';
import { EditorTable, useTableMapWizard } from '../context';
import { clampCenterToArea, Point, RoomRect, tableFootprintPx } from '../mapGeometry';
import { clientToSVG } from './MapEditorCanvas';
import TableFigure from './TableFigure';

const DRAG_THRESHOLD = 4;
const OVERLAP_COLOR = theme.palette.warning.dark;

interface PlacedTableProps {
    table: EditorTable;
    room: RoomRect;
    svgRef: React.RefObject<SVGSVGElement>;
    isOverlapping: boolean;
    isLocked: boolean;
    onSelect: (id: string) => void;
    onMove: (id: string, x: number, y: number) => void;
    children?: React.ReactNode;
}

export default function PlacedTable({
    table,
    room,
    svgRef,
    isOverlapping,
    isLocked,
    onSelect,
    onMove,
    children,
}: PlacedTableProps) {
    const { draft } = useTableMapWizard();
    const unit = useContext(CanvasUnitContext);
    const drag = useRef<{ start: Point; pointer: Point; moved: boolean } | null>(null);
    const { width, height } = tableFootprintPx(
        table.shape,
        table.width,
        table.height,
        draft.tableDefaults,
        room
    );
    const restingTransform = `translate(${room.x + table.x * room.width}, ${
        room.y + table.y * room.height
    })`;

    function pointerSvg(e: React.PointerEvent): Point | null {
        return svgRef.current ? clientToSVG(svgRef.current, e.clientX, e.clientY) : null;
    }

    function draggedCentre(pointer: Point): Point | null {
        const d = drag.current;
        if (!d) return null;
        return clampCenterToArea(
            d.start.x + (pointer.x - d.pointer.x) / room.width,
            d.start.y + (pointer.y - d.pointer.y) / room.height,
            draft.size,
            draft.area
        );
    }

    function handlePointerDown(e: React.PointerEvent<SVGGElement>) {
        const pointer = pointerSvg(e);
        if (!pointer) return;
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { start: { x: table.x, y: table.y }, pointer, moved: false };
    }

    function handlePointerMove(e: React.PointerEvent<SVGGElement>) {
        const d = drag.current;
        const pointer = pointerSvg(e);
        if (!d || !pointer || isLocked) return;
        const distance = Math.hypot(pointer.x - d.pointer.x, pointer.y - d.pointer.y);
        if (!d.moved && distance <= DRAG_THRESHOLD * unit) return;
        d.moved = true;
        const centre = draggedCentre(pointer);
        if (!centre) return;
        e.currentTarget.setAttribute(
            'transform',
            `translate(${room.x + centre.x * room.width}, ${room.y + centre.y * room.height})`
        );
    }

    function handlePointerUp(e: React.PointerEvent<SVGGElement>) {
        if (!drag.current) return;
        const pointer = pointerSvg(e);
        const centre = drag.current.moved && pointer ? draggedCentre(pointer) : null;
        drag.current = null;
        if (centre) onMove(table.id, centre.x, centre.y);
        else onSelect(table.id);
    }

    function handleDragAbort(e: React.PointerEvent<SVGGElement>) {
        if (!drag.current) return;
        drag.current = null;
        e.currentTarget.setAttribute('transform', restingTransform);
    }

    return (
        <g
            transform={restingTransform}
            style={{ cursor: isLocked ? 'pointer' : 'grab' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handleDragAbort}
            onLostPointerCapture={handleDragAbort}
        >
            <TableFigure
                width={width}
                height={height}
                seats={table.seats}
                shape={table.shape}
                label={table.label}
                isActive={table.isActive}
                color={isOverlapping ? OVERLAP_COLOR : undefined}
                rotation={table.rotation}
            />
            {children}
        </g>
    );
}
